import { useState } from 'react'

import BalticLogo from '@/components/BalticLogo'
import Button from '@/components/Button'
import Input from '@/components/Input'
import LanguageSelector from '@/components/LanguageSelector'
import OptimizedImage from '@/components/OptimizedImage'
import useTranslation from '@/hooks/useTranslation'
import usePageTitle from '@/hooks/usePageTitle'

function LoginForm({ onLogin, isLoggingIn, defaultPublicId = '' }) {
    const { t } = useTranslation()
    usePageTitle(t('auth.login'))
    const [publicId, setPublicId] = useState(defaultPublicId)
    const [pin, setPin] = useState('')
    const [error, setError] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [showForgotMessage, setShowForgotMessage] = useState(false)

    const handleSubmit = async (e) => {
        e.preventDefault()

        await onLogin(publicId, pin)
            .then(() => {
                localStorage.setItem('retailer_public_id', publicId)
            })
            .catch((err) => {
                if (err.retry_after) {
                    const minutes = Math.ceil(err.retry_after / 60)
                    setError(t('auth.tooManyAttempts', { minutes }))
                } else {
                    setError(err.error_message || t('auth.invalidCredentials'))
                }
            })
    }

    return (
        <div className="min-h-screen-safe grid grid-cols-1 lg:grid-cols-2 bg-white">
            <div className="flex flex-col items-center justify-center h-full p-5 relative">
                <form
                    onSubmit={handleSubmit}
                    className="w-full max-w-[380px] flex flex-col min-h-[400px]"
                >
                    <div className="mb-[40px] flex flex-col items-center justify-center gap-[8px]">
                        <BalticLogo size={22} />

                        <span className="text-[#000D1E66] lowercase tracking-[0.04em] text-[18.7px]">
                            {t('common.retailerPortal')}
                        </span>
                    </div>

                    <div className="flex flex-col gap-[12px] mb-[30px]">
                        <Input
                            placeholder={t('auth.retailerId')}
                            value={publicId}
                            onChange={(e) => {
                                const value = e.target.value
                                    .replace(/[^A-Za-z]/g, '')
                                    .toUpperCase()
                                setPublicId(value)
                            }}
                        />

                        <Input
                            placeholder={t('auth.pin')}
                            inputMode="numeric"
                            type={showPassword ? 'text' : 'password'}
                            value={pin}
                            onChange={(e) => {
                                const value = e.target.value
                                    .replace(/[^0-9]/g, '')
                                    .slice(0, 6)
                                setPin(value)
                            }}
                            error={error}
                            showPasswordToggle
                            isPasswordVisible={showPassword}
                            onTogglePasswordVisibility={() =>
                                setShowPassword(!showPassword)
                            }
                        />
                    </div>

                    <Button loading={isLoggingIn}>{t('auth.login')}</Button>

                    {!showForgotMessage && (
                        <span
                            className="text-dark-blue-40 text-center underline mt-[20px] text-xs cursor-pointer"
                            onClick={() => setShowForgotMessage(true)}
                        >
                            {t('auth.forgotCredentials')}
                        </span>
                    )}

                    {showForgotMessage && (
                        <p className="text-dark-blue-40 p-[16px] border border-dark-blue-20 rounded-[8px] text-[14px] mt-[20px]">
                            {t('auth.contactDistributor')}
                        </p>
                    )}
                </form>

                <div className="absolute bottom-5 left-5 right-5 flex justify-center lg:justify-start">
                    <LanguageSelector />
                </div>
            </div>

            <div className="p-5 hidden lg:block">
                <OptimizedImage
                    className="object-cover h-full w-full rounded-xl"
                    src="https://cdn.baltic-watches.com/f/73554/1000x1000/bef205e627/1_0007_sequence-02-00_07_16_03-still073-lumineux.png?width=1080"
                    alt="Baltic Watches"
                    sizes="50vw"
                />
            </div>
        </div>
    )
}

export default LoginForm
